import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

import { useEmbed } from "lib/context/embed-context";
import PoweredByZapier from "components/PoweredByZapier";
import { trpc } from "utils/trpc";
import { useTrackOpenAiUsage } from "../lib/useTrackOpenAiUsage";
import { useTrackingContext } from "observability";
import { ChatbotCreateEvent } from "../Chatbot/schema";
import { ChatbotPromptProps } from "../Chatbot/types";
import { Notification } from "blocks/components/Notification";
import { fetchWithProjectSession } from "lib/project-sessions/fetch";
import { usePageContext } from "lib/context/page-context";
import { useCurrentProject } from "lib/context/current-project-context";

type UseChatbotProps = Pick<ChatbotPromptProps, "blockId" | "config">;

export function useChatbotProps({
  blockId,
  config,
}: UseChatbotProps): ChatbotPromptProps {
  const { accountId, projectId, pageId, isEditing, isEmbedded, isThumbnail } =
    usePageContext();

  const { hideZapierButton } = useCurrentProject();

  const { mutateAsync: triggerBlockAction } =
    trpc.blockActions.createEvent.useMutation();

  const {
    emitAiTrackingPredictionUserFeedbackEvent,
    emitConsumerInteractionEvent,
  } = useTrackingContext();

  const { trackOpenAiUsage: onUsage } = useTrackOpenAiUsage(config);

  const triggerChatbotBlockAction = useCallback<
    (event: ChatbotCreateEvent, notificationMessage?: string) => Promise<void>
  >(
    async (event, notificationMessage) => {
      if (blockId && !isEditing) {
        await triggerBlockAction(event);

        if (notificationMessage) {
          toast.custom(
            (t) => (
              <Notification
                toast={t}
                message={notificationMessage}
                handleClose={toast.dismiss}
              />
            ),
            { position: "top-center" }
          );
        }
      }
    },
    [blockId, isEditing, triggerBlockAction]
  );
  const { isPopup, noBackground } = useEmbed();

  const renderPoweredByZapier = useCallback(
    () => (
      <PoweredByZapier
        alignment="center"
        position="relative"
        variant="light"
        withinContainer
      />
    ),
    []
  );

  return useMemo<ChatbotPromptProps>(
    () => ({
      accountId,
      blockId,
      config,
      emitAiTrackingPredictionUserFeedbackEvent,
      emitConsumerInteractionEvent,
      fetcher: fetchWithProjectSession,
      hideZapierButton,
      isDisabled: isThumbnail,
      isEditing,
      isEmbedded,
      isPopup,
      noBackground,
      onUsage,
      pageId,
      projectId,
      renderPoweredByZapier,
      triggerChatbotBlockAction,
    }),
    [
      accountId,
      blockId,
      config,
      emitAiTrackingPredictionUserFeedbackEvent,
      emitConsumerInteractionEvent,
      hideZapierButton,
      isEditing,
      isEmbedded,
      isPopup,
      isThumbnail,
      noBackground,
      onUsage,
      pageId,
      projectId,
      renderPoweredByZapier,
      triggerChatbotBlockAction,
    ]
  );
}
